import { useEffect, useMemo, useState, useRef } from "react";
import { sendMoment, sendWalkRequest } from "../../functions/api";
import { useNavigate } from "react-router-dom";
import { trackAction } from "../../utils/umami";
import { getRandomPastelColor } from "../../utils";
import { AppIcon } from "../../utils/appIcons";

const PeerReflectionsSection = ({
    peerReflections,
    loadingPeerReflections,
    setSelectedMomentReflection,
    setShowMomentModal,
    showMomentModal,
    selectedMomentReflection,
}) => {
    const navigate = useNavigate();
    const [momentMessage, setMomentMessage] = useState("");
    const [sendingMoment, setSendingMoment] = useState(false)
    const [momentType, setMomentType] = useState("");
    const [hasTypedMessage, setHasTypedMessage] = useState(false)

    const [showWalkModal, setShowWalkModal] = useState(false);
    const [selectedWalkReflection, setSelectedWalkReflection] = useState(null);
    const [walkMessage, setWalkMessage] = useState("");
    const [sendingWalkRequest, setSendingWalkRequest] = useState(false);

    const scrollContainerRef = useRef(null);


    /** Shuffled Reflections */
    const shuffledReflections = useMemo(() => {
        return peerReflections.slice().sort(() => Math.random() - 0.5);
    }, [peerReflections]);

    /** Send a Moment with a message */
    const handleSendMoment = async (reflection, type, message = "") => {
        trackAction(`Moment ${momentType} - Sending`)
        setSendingMoment(true)
        const userId = localStorage.getItem("breatheUserId") || "unknown_user";
        const momentData = {
            type,
            to: reflection?.userId,
            from: userId,
            reflection: {
                id: reflection?.id,
                snapshot: reflection?.reflection
            },
            message,
        };

        try {
            await sendMoment(momentData);
            trackAction(`Moment ${momentType} - Sent`)
            // alert(`Your ${type} moment was sent. 🌱`);
            setSendingMoment(false)
            setShowMomentModal(false);
            navigate("/connect?section=moments", { replace: true });
        } catch (error) {
            trackAction(`Error - Moment ${momentType} - Sent`)
            alert("Failed to send moment. Please try again.");
        }
    };

    const handleSendWalkRequest = async (reflection, message = "") => {
        if (!reflection) return;

        const senderId = localStorage.getItem("breatheUserId");
        if (!senderId) {
            alert("Please sign in to send a walk request.");
            return;
        }

        setSendingWalkRequest(true);
        trackAction("Walk Together - Request Sending");

        try {
            const receiverId = reflection.userId;
            const reflectionId = reflection.id;

            await sendWalkRequest({
                senderId,
                receiverId,
                reflectionId,
                message,
            });

            trackAction("Walk Together - Request Sent");
            setShowWalkModal(false);
            setWalkMessage("");
            setSelectedWalkReflection(null);
            navigate("/connect?section=walk-together", { replace: true });
        } catch (error) {
            trackAction("Walk Together - Request Error");
            alert(`${error?.response?.data?.error || "Failed to send walk request. Please try again."}`);
        } finally {
            setSendingWalkRequest(false);
        }
    };

    const hasFired = useRef({
        top: false,
        middle: false,
        bottom: false,
    });

    const handleScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight - container.clientHeight;

        const scrollPercent = (scrollTop / scrollHeight) * 100;

        if (scrollPercent > 10 && !hasFired.current.top) {
            trackAction("Peer Reflections - Scrolled 10%");
            hasFired.current.top = true;
        }
        if (scrollPercent > 50 && !hasFired.current.middle) {
            trackAction("Peer Reflections - Scrolled 50%");
            hasFired.current.middle = true;
        }
        if (scrollPercent > 90 && !hasFired.current.bottom) {
            trackAction("Peer Reflections - Scrolled 100%");
            hasFired.current.bottom = true;
        }
    };

    return (
        <>
            <section
                id="peer-reflections"
                ref={scrollContainerRef}
                onScroll={handleScroll}
                // className="flex-shrink-0 w-full max-w-xs snap-center flex flex-col items-center"
                className="snap-center w-screen flex-shrink-0 overflow-y-auto h-[85vh] px-4 py-6 max-w-[500px] 
                flex flex-col items-center"
            >
                <h2 className="text-2xl font-semibold mb-4 text-center">
                    Peer Reflections
                </h2>
                <p className="italic text-sm text-center text-[#6e6861] mb-4 inline-flex items-center gap-1.5">
                    “We breathe together.” <AppIcon name="breaths" size={14} />
                </p>
                {loadingPeerReflections ? (
                    <p className="italic text-gray-500">Loading...</p>
                ) : peerReflections.length === 0 ? (
                    <>
                        <p className="italic text-gray-500 mt-5">No one took the courage to share yet. </p>
                    </>
                ) : (
                    <div className="flex flex-col gap-0">
                        {shuffledReflections.map((reflection, index) => {

                            return (
                                <div
                                    key={reflection?.id}
                                    className="bg-[#fff9f3] rounded-2xl p-4 shadow-md border border-[#e4dfd8] hover:shadow-lg transition mb-5"
                                >

                                    {/* Reflection Text */}
                                    <p className="text-base leading-relaxed mb-2">
                                        {reflection?.reflection}
                                    </p>

                                    {/* Alias (if present) */}
                                    {reflection.alias && (
                                        <p className="text-xs italic text-gray-500 mb-2">
                                            — {reflection?.alias}
                                        </p>
                                    )}

                                    {/* Feeling */}
                                    <p className="text-sm text-gray-500 italic mb-3">
                                        Feeling: {reflection?.feeling}
                                    </p>

                                    {/* Tags */}
                                    <div className="flex flex-wrap  gap-2 mb-5">
                                        {reflection?.tags.map((tag) => (
                                            // <span
                                            //     key={tag}
                                            //     className="bg-[#ece8e1] text-xs text-[#6e6861] px-2 py-1 rounded-full"
                                            // >
                                            //     #{tag}
                                            // </span>
                                            <span
                                                key={tag}
                                                style={{
                                                    backgroundColor: getRandomPastelColor() || "#ece8e1",
                                                    color: "#3c3a37",
                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                }}
                                                className="text-xs px-2 py-1 rounded-full transition-colors"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Buttons */}
                                    <div className="grid grid-cols-3 gap-2 w-full">
                                        <button
                                            onClick={() => {
                                                trackAction(`Peer Reflections - 🤝 Relate`)
                                                setSelectedMomentReflection(reflection);
                                                setMomentType("relate");
                                                setMomentMessage("");
                                                setShowMomentModal(true);
                                            }}
                                            className="min-w-0 inline-flex items-center justify-center gap-1.5 bg-[#e9eef1] hover:bg-[#dde7eb] text-[#557181] text-xs sm:text-sm leading-tight text-center px-2 py-2 rounded-full shadow-sm transition"
                                        >
                                            <span className="shrink-0"><AppIcon name="offer" size={15} /></span>
                                            Relate
                                        </button>
                                        <button
                                            onClick={() => {
                                                trackAction(`Peer Reflections - 💛 Send Warmth`)
                                                setSelectedMomentReflection(reflection);
                                                setMomentType("warmth");
                                                setMomentMessage("");
                                                setShowMomentModal(true);
                                            }}
                                            className="min-w-0 inline-flex items-center justify-center gap-1.5 bg-[#f4e7e8] hover:bg-[#eedcde] text-[#985f6d] text-xs sm:text-sm leading-tight text-center px-2 py-2 rounded-full shadow-sm transition"
                                        >
                                            <span className="shrink-0"><AppIcon name="warmth" size={15} /></span>
                                            Send Warmth
                                        </button>
                                        <button
                                            onClick={() => {
                                                trackAction("Peer Reflections - 🌿 Walk Together");
                                                setSelectedWalkReflection(reflection);
                                                setWalkMessage("");
                                                setShowWalkModal(true);
                                            }}
                                            className="min-w-0 inline-flex items-center justify-center gap-1.5 bg-[#e7efe4] hover:bg-[#dce9d8] text-[#5f8059] text-xs sm:text-sm leading-tight text-center px-2 py-2 rounded-full shadow-sm transition"
                                        >
                                            <span className="shrink-0"><AppIcon name="walk" size={15} /></span>
                                            Walk Together
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Moment Modal */}
                {showMomentModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                        <div className={`rounded-2xl p-6 max-w-md w-full shadow-lg border ${momentType === "warmth"
                            ? "bg-[#f4e7e8] border-[#eedcde]"
                            : "bg-[#e9eef1] border-[#dde7eb]"
                            }`}>
                            <h2 className="text-xl font-semibold mb-4 text-center inline-flex items-center gap-2">
                                <AppIcon name={momentType === "warmth" ? "warmth" : "offer"} size={19} />
                                {momentType === "warmth" ? "Send Warmth" : "Send Relate"}
                            </h2>
                            <textarea
                                value={momentMessage}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setMomentMessage(e.target.value)
                                    if (!hasTypedMessage && value.trim() !== "") {
                                        trackAction(`Moment ${momentType} - Typing Message`);
                                        setHasTypedMessage(true);
                                    }
                                }}
                                rows={3}
                                className="w-full border px-3 py-2 rounded mb-4"
                                placeholder="Write an optional message..."
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        trackAction(`Moment ${momentType} - Cancel`)
                                        setShowMomentModal(false)
                                    }}
                                    className="px-4 py-2 text-sm rounded bg-gray-200 hover:bg-gray-300 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        handleSendMoment(selectedMomentReflection, momentType, momentMessage);
                                    }}
                                    disabled={sendingMoment}
                                    className={`px-4 py-2 text-sm rounded transition ${momentType === "warmth"
                                        ? "bg-[#eedcde] hover:bg-[#e6cdd0] text-[#985f6d]"
                                        : "bg-[#dde7eb] hover:bg-[#cfdee3] text-[#557181]"
                                        }`}
                                >
                                    {sendingMoment ? "Sending..." : "Send"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Walk Together Modal */}
                {showWalkModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                        <div className="bg-[#e7efe4] border border-[#dce9d8] rounded-2xl p-6 max-w-md w-full shadow-lg">
                            <h2 className="text-xl font-semibold mb-4 text-center inline-flex items-center gap-2">
                                <AppIcon name="walk" size={19} />
                                Walk Together
                            </h2>
                            <p className="text-sm text-gray-600 mb-4 text-center">
                                Send a walk request to the author of this reflection.
                            </p>
                            <textarea
                                value={walkMessage}
                                onChange={(e) => setWalkMessage(e.target.value)}
                                rows={3}
                                className="w-full border px-3 py-2 rounded mb-4"
                                placeholder="Write an optional message..."
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setShowWalkModal(false);
                                        setSelectedWalkReflection(null);
                                    }}
                                    className="px-4 py-2 text-sm rounded bg-gray-200 hover:bg-gray-300 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        handleSendWalkRequest(selectedWalkReflection, walkMessage);
                                    }}
                                    disabled={sendingWalkRequest}
                                    className="px-4 py-2 text-sm rounded bg-[#dce9d8] hover:bg-[#cfe2ca] text-[#5f8059] transition"
                                >
                                    {sendingWalkRequest ? "Sending..." : "Send Walk Request"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </section>


        </>

    );
};

export default PeerReflectionsSection;
